/**
 * Paying for a card (CR §4.3.1–§4.3.3, SPEC.md §4.1's cost system).
 *
 * `costs.ts` says what a card costs; this module says **how it may be paid, and pays it**.
 * The CR plays a card in four beats — announce the card, announce the payment method, compute
 * the total cost, pay it in full — and the second beat is a *choice*. SPEC.md §4.3 has exactly
 * one shape for a choice, so every legal payment is enumerated up front as its own keyed
 * `play` option rather than as a follow-up question:
 *
 * ```
 * play:d42                 pay 42's ink cost
 * play:d42:free            play it for free (CR §6.1.7)
 * play:d42:sing:i7         sing it, exerting the character with instance id 7 (CR §5.4.4)
 * play:d42:sing:i7+i9      Sing Together, exerting both (CR §8.12)
 * play:d42:shift:i3        Shift it onto instance 3 (CR §8.10) — see the gate below
 * ```
 *
 * The card being played is keyed by **`defId`** because a hand is hidden and two copies are
 * the same move; everything a payment *names* is public (in play, or in a discard), so it is
 * keyed by **instance id** (SPEC.md §4.4). A view therefore never has to redact a payment
 * option, and the AI's move list and the UI's affordances stay the same array.
 *
 * ## Shift
 *
 * A Shift payment is an ordinary alternate cost here — its ink is paid like any other — and
 * the *board object* it produces is `stacks.ts`: the card goes on top of its bases, which stop
 * being in play (CR §8.10.4). That half runs from `playCard` rather than from this module,
 * because a card has to be **in play** before it can be the top of a pile, and `payFor` runs
 * one beat earlier, before the card moves (CR §4.3.3).
 */
import type { Selector } from './abilities/ir.ts';
import { baseContext, selectorPool } from './abilities/selectors.ts';
import { canExertForAction, canPayExertCost, definitionOf } from './characteristics.ts';
import type { ChoiceOption } from './choices.ts';
import { option } from './choices.ts';
import type { PaymentPlan } from './costs.ts';
import {
  alternateCostOf,
  canPlayForFree,
  consumeOneShotModifiers,
  costOf,
  isPlayForbidden,
  isSong,
  shiftBaseSets,
  shiftTotalCostOf,
  singCostOf,
  singerContributionOf,
  singTogetherRequirementOf,
  zonePlayPermissionFor,
} from './costs.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import { isRestricted } from './modifiers.ts';
import type { CardInstance, GameState } from './state.ts';
import { instanceAt, playerAt } from './state.ts';
import {
  availableInk,
  banishEvent,
  cardsIn,
  exertCharacter,
  findInHandByDef,
  payInk,
  putOnDeck,
} from './zones.ts';

/**
 * Whether a Shift payment may be taken. True since ticket 12 built the stack it produces
 * (`stacks.ts`); kept as a named constant because it is the one switch that decides whether
 * the engine offers Shift plays at all.
 */
export const SHIFT_PLAYS_ENABLED: boolean = true;

/**
 * How many distinct singer combinations one song offers, and how much of the subset lattice
 * is walked to find them. Sing Together is "any number of your characters", so the legal
 * payments are genuinely the power set; these bounds keep a pathological board from turning
 * one option list into thousands of edges for the search. Combinations are emitted smallest
 * first, so the bound drops the most redundant payments, and neither is reachable with a
 * plausible board (they need 8+ simultaneously ready singers).
 */
const MAX_SINGER_COMBINATIONS = 256;
const MAX_SINGER_SUBSET_VISITS = 4096;

/**
 * The same bound for a **banish** alternate cost and for a banish activation cost, whose
 * option lists are subsets of the cards you control. Two of six items is 15 options; the bound
 * is only ever reached by a board no game produces.
 */
export const MAX_BANISH_COMBINATIONS = 128;

/** What a completed payment did — the ink it spent and the characters that sang. */
export interface PaymentResult {
  readonly inkPaid: number;
  readonly singers: readonly InstanceId[];
}

// ---------------------------------------------------------------------------
// Enumerating the legal payments
// ---------------------------------------------------------------------------

/**
 * Every legal way this player may pay for this card, in a fixed order: ink, free, singing
 * (fewest singers first), Shift. Two identical states enumerate identically.
 *
 * A card playable **only** through an alternate cost — a song you cannot afford but can sing —
 * appears here exactly when that alternate cost is payable, which is the whole point of
 * enumerating payments rather than asking about them afterwards.
 */
export function paymentPlansFor(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): PaymentPlan[] {
  const plans: PaymentPlan[] = [];
  if (costOf(state, card) <= availableInk(state, player)) plans.push({ kind: 'ink' });
  if (canPlayForFree(state, player, card)) plans.push({ kind: 'free' });
  for (const singers of singPayments(state, player, card)) plans.push({ kind: 'sing', singers });
  if (SHIFT_PLAYS_ENABLED) {
    for (const onto of shiftPayments(state, player, card)) plans.push({ kind: 'shift', onto });
    for (const each of shiftAlternateCostPayments(state, player, card)) {
      plans.push({ kind: 'shift-alternate-cost', onto: each.onto, method: each.method, cards: each.cards });
    }
  }
  for (const banish of banishPayments(state, player, card)) plans.push({ kind: 'banish', banish });
  for (const exert of exertPayments(state, player, card)) plans.push({ kind: 'exert', exert });
  for (const cards of moveToBottomOfDeckPayments(state, player, card)) {
    plans.push({ kind: 'move-to-bottom-of-deck', cards });
  }
  return plans;
}

/**
 * Every set of cards this player may banish as this card's alternate cost (CR §1.5.5) —
 * "banish chosen item of yours to play this character for free". Sets are canonical (ascending
 * by instance id) and enumerated in pool order, so two identical states list identically.
 */
function banishPayments(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): InstanceId[][] {
  if (card.zone !== 'hand' || card.controller !== player) return [];
  const spec = alternateCostOf(state, card);
  // A `for: 'shift'` alternate cost only ever substitutes for the Shift cost
  // (`shiftAlternateCostPayments`), never a standalone free play (ticket 136).
  if (spec === null || spec.method !== 'banish' || spec.for !== 'play') return [];
  if (spec.window === 'your-turn' && state.activePlayer !== player) return [];
  const pool = banishablePool(state, player, card, spec.selector);
  return combinationsOf(pool, spec.count, MAX_BANISH_COMBINATIONS);
}

/** The cards a `banish` alternate cost may take — its selector's pool, read from this seat. */
function banishablePool(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  filter: Selector,
): InstanceId[] {
  return selectorPool(state, baseContext(card.id, player, null), filter).filter(
    (id) => instanceAt(state, id).zone === 'play',
  );
}

/**
 * Every set of cards this player may exert as this card's alternate cost (CR §1.5.5) — "exert
 * 4 items of yours to play this character for free" (Scrooge McDuck - Resourceful Miser's PUT
 * IT TO GOOD USE). `banishPayments`'s exact mirror: sets canonical, enumerated in pool order.
 */
function exertPayments(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): InstanceId[][] {
  if (card.zone !== 'hand' || card.controller !== player) return [];
  const spec = alternateCostOf(state, card);
  if (spec === null || spec.method !== 'exert' || spec.for !== 'play') return [];
  if (spec.window === 'your-turn' && state.activePlayer !== player) return [];
  const pool = exertablePool(state, player, card, spec.selector);
  return combinationsOf(pool, spec.count, MAX_BANISH_COMBINATIONS);
}

/**
 * The cards an `exert` alternate cost may take — its selector's pool, restricted to cards that
 * can actually pay `{E}` (CR §4.4.2): a character candidate must be dry, an item or location
 * need not be. Mirrors `banishablePool`, but with `{E}`'s own eligibility bar rather than
 * "anything in play".
 */
function exertablePool(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  filter: Selector,
): InstanceId[] {
  return selectorPool(state, baseContext(card.id, player, null), filter).filter((id) =>
    canPayExertCost(state, instanceAt(state, id)),
  );
}

/**
 * Every set of public discard cards this player may move to the bottom of their deck as this
 * card's alternate cost. The zone and controller checks are repeated here even though the
 * authored selector states them: payment legality must never be widened by an underspecified
 * selector, especially across a hidden hand/public discard boundary.
 */
function moveToBottomOfDeckPayments(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): InstanceId[][] {
  if (card.zone !== 'hand' || card.controller !== player) return [];
  const spec = alternateCostOf(state, card);
  if (spec === null || spec.method !== 'move-to-bottom-of-deck' || spec.for !== 'play') return [];
  if (spec.window === 'your-turn' && state.activePlayer !== player) return [];
  const pool = discardMoveablePool(state, player, card, spec.selector);
  return combinationsOf(pool, spec.count, MAX_BANISH_COMBINATIONS);
}

/**
 * The cards a `move-to-bottom-of-deck` alternate cost may take — its selector's pool, restricted
 * to the paying controller's **public discard**. Repeated here rather than trusted to the
 * authored selector alone, same reasoning as `banishablePool`/`exertablePool`: payment legality
 * must never be widened by an underspecified selector, especially across a hidden hand/public
 * discard boundary. Shared by the ordinary `move-to-bottom-of-deck` payment and its Shift
 * sibling (`shiftAlternateCostPayments`, ticket 136) — both name the same pool.
 */
function discardMoveablePool(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  filter: Selector,
): InstanceId[] {
  return selectorPool(state, baseContext(card.id, player, null), filter).filter((id) => {
    const candidate = instanceAt(state, id);
    return candidate.zone === 'discard' && candidate.controller === player;
  });
}

/** Every `size`-card subset, ascending, bounded so a wide board cannot explode the option list. */
function combinationsOf(
  pool: readonly InstanceId[],
  size: number,
  limit: number,
): InstanceId[][] {
  if (size <= 0 || pool.length < size) return [];
  const found: InstanceId[][] = [];
  const chosen: InstanceId[] = [];
  const walk = (start: number): void => {
    if (found.length >= limit) return;
    if (chosen.length === size) {
      found.push([...chosen].sort((a, b) => a - b));
      return;
    }
    for (let index = start; index < pool.length; index += 1) {
      chosen.push(pool[index] as InstanceId);
      walk(index + 1);
      chosen.pop();
      if (found.length >= limit) return;
    }
  };
  walk(0);
  return found;
}

/** The `play` options for every card in this player's hand — what `legalTurnActions` shows. */
export function legalPlayOptions(state: GameState, player: PlayerIndex): ChoiceOption[] {
  const options: ChoiceOption[] = [];
  for (const defId of distinctHandDefIds(state, player)) {
    const card = findInHandByDef(state, player, defId);
    if (card === undefined) continue;
    // "Opponents can't play actions" — a restriction on the card being played, read where it
    // is (CR §6.6.5). No payment method gets round it, whether it comes from a card in play
    // (the static) or from a resolved effect with a duration (the payment record).
    if (isRestricted(state, card, 'play')) continue;
    if (isPlayForbidden(state, player, card)) continue;
    for (const plan of paymentPlansFor(state, player, card)) {
      options.push(
        option(plan.kind === 'ink' ? { kind: 'play', defId } : { kind: 'play', defId, payment: plan }),
      );
    }
  }
  return options;
}

/**
 * The `play-from-zone` options for every discard card a continuous zone-play permission covers
 * right now (ticket 140) — The Horned King - Merciless Master's CAULDRON'S POWER, "you may play
 * characters from your discard". A discard is public, so unlike `legalPlayOptions`'
 * defId-collapsed hand list, each instance is its own option (SPEC.md §4.4).
 *
 * Scoped to the discard because that is the only zone any printed permission reaches so far;
 * `zonePlayPermissionFor` itself reads `Zone` generically, so widening this to another public
 * zone later is a matter of scanning it here too, not a new IR shape.
 */
export function legalZonePlayOptions(state: GameState, player: PlayerIndex): ChoiceOption[] {
  const options: ChoiceOption[] = [];
  for (const card of cardsIn(state, player, 'discard')) {
    const permission = zonePlayPermissionFor(state, player, card);
    if (permission === null) continue;
    // Same two checks `legalPlayOptions` makes for a hand play — a play restriction or a
    // "can't play" veto applies wherever the card is being played from, permission or not.
    if (isRestricted(state, card, 'play')) continue;
    if (isPlayForbidden(state, player, card)) continue;
    if (permission.cost === 'full' && costOf(state, card) > availableInk(state, player)) continue;
    options.push(option({ kind: 'play-from-zone', instance: card.id, free: permission.cost === 'free' }));
  }
  return options;
}

/** Distinct printed ids in a player's hand, ascending — the hidden-zone option domain. */
function distinctHandDefIds(state: GameState, player: PlayerIndex): number[] {
  const seen = new Set<number>();
  for (const id of playerAt(state, player).hand) seen.add(instanceAt(state, id).defId);
  return [...seen].sort((a, b) => a - b);
}

/**
 * Every legal set of singers for this card (CR §5.4.4, §8.12), smallest set first.
 *
 * A single character may sing a song whose singing requirement its **Singer value** reaches
 * (its printed cost, or its Singer N if that is higher). A song with **Sing Together N** adds
 * every combination whose values total N or more — the same character pool, exerted together.
 * One set can be legal both ways; it is offered once.
 */
function singPayments(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): InstanceId[][] {
  if (card.zone !== 'hand' || !isSong(definitionOf(state, card))) return [];
  const candidates = eligibleSingers(state, player, card);
  if (candidates.length === 0) return [];

  const found: InstanceId[][] = [];
  const seen = new Set<string>();
  const push = (ids: readonly InstanceId[]): void => {
    // Deduped by *set*, because one combination can be legal both as a solo sing and as Sing
    // Together, and because the option key names the same set whatever order it was built in.
    const key = [...ids].sort((a, b) => a - b).join('+');
    if (seen.has(key)) return;
    seen.add(key);
    found.push([...ids]);
  };

  const solo = singCostOf(state, card);
  for (const singer of candidates) {
    if (singer.value >= solo) push([singer.id]);
  }
  const together = singTogetherRequirementOf(state, card);
  if (together !== null) {
    for (const combination of singerCombinations(candidates, together)) push(combination);
  }
  return found;
}

/**
 * The characters this player may exert to sing `song`: in play, dry and ready (CR §5.4.4) —
 * and not one a continuous effect forbids it to, whether unconditionally ("this character
 * can't ⟳ to sing songs") or conditionally on this particular song ("…can't sing songs without
 * Sing Together", Meilin Lee - Lead Vocalist, ticket 139 — read via `isRestricted`'s `sing`
 * restriction with `song` as the second card, so its `by` selector can bar songs by keyword).
 */
function eligibleSingers(
  state: GameState,
  player: PlayerIndex,
  song: CardInstance,
): { readonly id: InstanceId; readonly value: number }[] {
  const found: { id: InstanceId; value: number }[] = [];
  for (const id of playerAt(state, player).play) {
    const card = instanceAt(state, id);
    if (!canExertForAction(state, card)) continue;
    if (isRestricted(state, card, 'sing', song)) continue;
    found.push({ id, value: singerContributionOf(state, card) });
  }
  return found;
}

/**
 * Every combination of singers whose values reach `required`, in ascending-size and then
 * ascending-id order. A depth-first walk over the subset lattice, bounded by the two budgets
 * above — supersets of a legal set are legal too (each singer meets "whenever this character
 * sings"), so they are offered rather than pruned.
 */
function singerCombinations(
  candidates: readonly { readonly id: InstanceId; readonly value: number }[],
  required: number,
): InstanceId[][] {
  const bySize: InstanceId[][][] = [];
  let visits = 0;
  const chosen: InstanceId[] = [];

  const walk = (start: number, total: number): void => {
    for (let index = start; index < candidates.length; index += 1) {
      if (visits >= MAX_SINGER_SUBSET_VISITS) return;
      visits += 1;
      const candidate = candidates[index];
      if (candidate === undefined) continue;
      chosen.push(candidate.id);
      const sum = total + candidate.value;
      if (sum >= required) {
        const bucket = (bySize[chosen.length - 1] ??= []);
        bucket.push([...chosen]);
      }
      walk(index + 1, sum);
      chosen.pop();
    }
  };
  walk(0, 0);

  const found: InstanceId[][] = [];
  for (let size = 0; size < bySize.length; size += 1) {
    const bucket = bySize[size];
    if (bucket === undefined) continue;
    for (const combination of bucket) {
      if (found.length >= MAX_SINGER_COMBINATIONS) return found;
      found.push(combination);
    }
  }
  return found;
}

/**
 * The Shift base sets this player may pay for right now (CR §8.10), each priced against the
 * base it names — an onto-keyed modifier ("you pay 1 {I} less to shift a character on top of
 * this character") can make one legal base affordable while another, otherwise identical, stays
 * out of reach, so affordability is checked per candidate rather than once for the whole card.
 */
function shiftPayments(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): InstanceId[][] {
  if (card.zone !== 'hand' || card.controller !== player) return [];
  const ink = availableInk(state, player);
  return shiftBaseSets(state, card).filter((onto) => {
    const cost = shiftTotalCostOf(state, card, onto);
    return cost !== null && cost <= ink;
  });
}

/**
 * Every Shift base × alternate-cost candidate-set pairing this player may pay right now
 * (ticket 136) — "you may put 5 character cards from your discard on the bottom of your deck
 * in any order to shift this character for free" (Maleficent & Diablo - Evil Incarnate's
 * FOOLS!). The base a Shift lands on (`shiftBaseSets`) and what pays for it
 * (`alternateCostOf`'s payment) are independent questions, so every legal base pairs with every
 * legal candidate set, exactly the way an ordinary Shift's ink cost pairs with every base
 * regardless of how much ink is in the pool.
 *
 * Only offered for a `for: 'shift'` alternate cost — a `for: 'play'` one never reaches here
 * (`banishPayments`/`exertPayments`/`moveToBottomOfDeckPayments` own that instead), so a card
 * never offers both this and a standalone free play from the same static.
 */
function shiftAlternateCostPayments(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): { onto: InstanceId[]; method: 'banish' | 'exert' | 'move-to-bottom-of-deck'; cards: InstanceId[] }[] {
  if (card.zone !== 'hand' || card.controller !== player) return [];
  const spec = alternateCostOf(state, card);
  if (spec === null || spec.for !== 'shift') return [];
  if (spec.window === 'your-turn' && state.activePlayer !== player) return [];
  const bases = shiftBaseSets(state, card);
  if (bases.length === 0) return [];
  const pool =
    spec.method === 'banish'
      ? banishablePool(state, player, card, spec.selector)
      : spec.method === 'exert'
        ? exertablePool(state, player, card, spec.selector)
        : discardMoveablePool(state, player, card, spec.selector);
  const candidateSets = combinationsOf(pool, spec.count, MAX_BANISH_COMBINATIONS);
  if (candidateSets.length === 0) return [];
  const found: { onto: InstanceId[]; method: typeof spec.method; cards: InstanceId[] }[] = [];
  for (const onto of bases) {
    for (const cards of candidateSets) found.push({ onto, method: spec.method, cards });
  }
  return found;
}

// ---------------------------------------------------------------------------
// Checking and taking a payment
// ---------------------------------------------------------------------------

/**
 * Whether this payment is legal right now — the pre-validation that replaces the CR's
 * rewind-on-illegal-payment semantics (§1.7.6). `legalPlayOptions` only ever offers payments
 * that satisfy this, and `playCard` re-checks, so an engine bug surfaces as a throw rather
 * than as a card played for nothing.
 */
export function canPayFor(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  plan: PaymentPlan,
): boolean {
  switch (plan.kind) {
    case 'ink':
      return costOf(state, card) <= availableInk(state, player);
    case 'free':
      return canPlayForFree(state, player, card);
    case 'sing':
      return isLegalSinging(state, player, card, plan.singers);
    case 'shift': {
      const wanted = [...plan.onto].sort((a, b) => a - b).join('+');
      if (!shiftBaseSets(state, card).some((each) => each.join('+') === wanted)) return false;
      const cost = shiftTotalCostOf(state, card, plan.onto);
      return cost !== null && cost <= availableInk(state, player);
    }
    case 'banish': {
      const wanted = [...plan.banish].sort((a, b) => a - b).join('+');
      return banishPayments(state, player, card).some((each) => each.join('+') === wanted);
    }
    case 'exert': {
      const wanted = [...plan.exert].sort((a, b) => a - b).join('+');
      return exertPayments(state, player, card).some((each) => each.join('+') === wanted);
    }
    case 'move-to-bottom-of-deck': {
      const wanted = [...plan.cards].sort((a, b) => a - b).join('+');
      return moveToBottomOfDeckPayments(state, player, card).some(
        (each) => each.join('+') === wanted,
      );
    }
    case 'shift-alternate-cost': {
      const wantedOnto = [...plan.onto].sort((a, b) => a - b).join('+');
      const wantedCards = [...plan.cards].sort((a, b) => a - b).join('+');
      return shiftAlternateCostPayments(state, player, card).some(
        (each) =>
          each.method === plan.method &&
          each.onto.join('+') === wantedOnto &&
          each.cards.join('+') === wantedCards,
      );
    }
  }
}

/** Whether these characters may sing this card together (CR §5.4.4, §8.11, §8.12). */
function isLegalSinging(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  singers: readonly InstanceId[],
): boolean {
  if (singers.length === 0 || !isSong(definitionOf(state, card))) return false;
  if (new Set(singers).size !== singers.length) return false;
  let total = 0;
  for (const id of singers) {
    const singer = instanceAt(state, id);
    if (singer.controller !== player || !canExertForAction(state, singer)) return false;
    if (isRestricted(state, singer, 'sing', card)) return false;
    total += singerContributionOf(state, singer);
  }
  const only = singers[0];
  if (singers.length === 1 && only !== undefined) {
    if (singerContributionOf(state, instanceAt(state, only)) >= singCostOf(state, card)) return true;
  }
  const together = singTogetherRequirementOf(state, card);
  return together !== null && total >= together;
}

/**
 * Take the payment. The caller has already checked it with `canPayFor`; this is the "pay the
 * total cost in full" beat (CR §4.3.3), and it is the last moment before the card moves.
 *
 * One-shot payment modifiers are spent here **whichever method was used** — CR 2.2 has a card
 * played for free still allow its payment modifiers to have applied, so a floating "the next
 * character you play this turn costs 2 {I} less" does not survive a free play.
 */
export function payFor(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  plan: PaymentPlan,
): PaymentResult {
  if (!canPayFor(state, player, card, plan)) {
    throw new Error(`Player ${player} cannot pay for card ${card.defId} that way`);
  }
  const result = takePayment(state, player, card, plan);
  consumeOneShotModifiers(state, player, card, 'play');
  return result;
}

function takePayment(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  plan: PaymentPlan,
): PaymentResult {
  switch (plan.kind) {
    case 'ink': {
      const cost = costOf(state, card);
      payInk(state, player, cost);
      return { inkPaid: cost, singers: [] };
    }
    case 'free':
      return { inkPaid: 0, singers: [] };
    case 'sing': {
      // Singing is an alternate cost, so no ink is paid at all (CR §5.4.4).
      for (const id of plan.singers) exertCharacter(state, id);
      return { inkPaid: 0, singers: [...plan.singers] };
    }
    case 'shift': {
      // Shift is an alternate cost paid in ink (CR §8.10.1); putting the card on top of its
      // bases is what happens *after* it enters play, and belongs to `playCard`/`stacks.ts`.
      const cost = shiftTotalCostOf(state, card, plan.onto);
      if (cost === null) throw new Error(`Card ${card.defId} has no Shift ability`);
      payInk(state, player, cost);
      return { inkPaid: cost, singers: [] };
    }
    case 'banish': {
      // An alternate cost, so no ink is paid at all — the banished cards *are* the payment.
      for (const id of plan.banish) banishEvent(state, id, card.id);
      return { inkPaid: 0, singers: [] };
    }
    case 'exert': {
      // An alternate cost, so no ink is paid at all — the exerted cards *are* the payment.
      // `exertCharacter` only emits `character-exerted` for a character candidate (`zones.ts`),
      // so exerting an item here raises no event a "becomes exerted" trigger could see.
      for (const id of plan.exert) exertCharacter(state, id);
      return { inkPaid: 0, singers: [] };
    }
    case 'move-to-bottom-of-deck': {
      // The named cards are the alternate payment, so no ink is spent. `putOnDeck` performs
      // the ordinary owner-returning zone move and preserves the deck's ordering contract.
      for (const id of plan.cards) putOnDeck(state, id, 'bottom');
      return { inkPaid: 0, singers: [] };
    }
    case 'shift-alternate-cost': {
      // The alternate cost pays instead of Shift's ink cost (CR §1.5.5 layered onto CR §8.10) —
      // no ink is spent. Putting the card on top of `plan.onto` is `playCard`'s job, the same
      // as an ordinary `shift` payment; this only takes the substitute payment itself.
      switch (plan.method) {
        case 'banish':
          // Replaceable, exactly like the ordinary `banish` alternate cost above (ticket 137).
          for (const id of plan.cards) banishEvent(state, id, card.id);
          break;
        case 'exert':
          for (const id of plan.cards) exertCharacter(state, id);
          break;
        case 'move-to-bottom-of-deck':
          for (const id of plan.cards) putOnDeck(state, id, 'bottom');
          break;
      }
      return { inkPaid: 0, singers: [] };
    }
  }
}
